import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Clock, Users, Star, Quote, MessageSquarePlus, ExternalLink } from "lucide-react";
import { IMAGES, TESTIMONIALS, ROUTE_STOPS, SCHEDULE, GOOGLE_REVIEW_URL } from "@/data/content";
import { fetchPublicReviews } from "@/lib/api";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import MartinIntroVideo from "@/components/MartinIntroVideo";
import { useEffect, useState } from "react";

export default function Home() {
  const [tIndex, setTIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTIndex((i) => (i + 1) % TESTIMONIALS.length), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[620px] w-full overflow-hidden">
        <img
          src={IMAGES.heroFlinders}
          alt="Flinders Street Station"
          className="absolute inset-0 w-full h-full object-cover sepia-soft"
        />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-24">
          <div className="max-w-3xl float-in">
            <div className="stamp bg-[#FDFBF7]/85" data-testid="hero-eyebrow">
              <Star size={12} className="text-[#D4AF37]" fill="#D4AF37" /> Est. 2026 · Melbourne CBD
            </div>
            <h1 className="mt-6 text-[#FDFBF7] serif text-5xl md:text-7xl lg:text-8xl leading-[0.95]">
              Marvellous Melbourne,<br />
              <span className="italic text-[#E7C766]">on foot with Martin.</span>
            </h1>
            <p className="mt-6 text-[#F5EFEB] max-w-2xl text-base md:text-lg leading-relaxed">
              A two-hour storytelling walk through the gold rush arcades, boom-era banks
              and stubborn little laneways of Melbourne's CBD. One tour. One guide.
              Countless surprising stories.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/individual" className="btn-primary" data-testid="hero-book-btn">
                Book a Tour <ArrowRight size={18} />
              </Link>
              <Link to="/corporate" className="btn-outline-gold bg-[#FDFBF7]/90" data-testid="hero-enquire-btn">
                Enquire for a Group
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK FACTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-14 relative z-20" data-testid="quick-facts">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: <Clock />, label: "Duration", value: "≈ 2 hours" },
            { icon: <MapPin />, label: "Route", value: "7 storied CBD streets" },
            { icon: <Users />, label: "Group size", value: "Up to 30" },
          ].map((f, i) => (
            <div key={i} className="card-warm rounded-2xl p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#6E2638] text-[#FDFBF7] flex items-center justify-center">
                {f.icon}
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">{f.label}</div>
                <div className="serif text-2xl text-[#6E2638]">{f.value}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MARTIN INTRO VIDEO */}
      <MartinIntroVideo />

      {/* FEATURED TOUR */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="featured-tour">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative">
            <img src={IMAGES.collinsDome} alt="Historic Collins Street"
              className="rounded-2xl w-full h-[520px] object-cover sepia-soft" />
            <div className="absolute -bottom-8 -right-4 hidden md:block card-warm rounded-2xl p-6 max-w-xs">
              <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">From</div>
              <div className="serif text-4xl text-[#6E2638]">$35<span className="text-xl"> AUD</span></div>
              <div className="text-sm text-[#1A1817]/70">per adult · under 12s walk free</div>
            </div>
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <div className="stamp" data-testid="featured-eyebrow">Featured Walk</div>
            <h2 className="serif text-4xl md:text-5xl text-[#6E2638] mt-4 leading-tight">
              Marvellous Melbourne with Martin
            </h2>
            <p className="mt-5 text-[#1A1817]/85 leading-relaxed">
              From Federation Square's steps to the columns of Parliament,
              Martin weaves the gold rush, the boom, the bust, and the beautifully
              stubborn architecture that survived it all. Bring good shoes and a curious head.
            </p>
            <ul className="mt-6 space-y-3">
              {SCHEDULE.map((s) => (
                <li key={s.day} className="flex justify-between border-b border-[#E5D9C5] pb-2 text-sm">
                  <span className="serif text-[#1E4637] text-lg">{s.day}</span>
                  <span className="text-[#6E2638] font-medium">{s.time}</span>
                </li>
              ))}
            </ul>
            <Link to="/individual" className="btn-primary mt-8" data-testid="featured-book-btn">
              Book this walk <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ROUTE */}
      <section className="bg-[#F5EFEB] border-y border-[#E5D9C5] py-24" data-testid="route-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="stamp">The Route</div>
            <h2 className="serif text-4xl md:text-5xl text-[#6E2638] mt-4">Five streets. One unforgettable city.</h2>
          </div>
          <OrnamentalDivider label="Melbourne CBD" />
          <div className="grid md:grid-cols-5 gap-6 mt-6">
            {ROUTE_STOPS.map((r, i) => (
              <div key={r.street} className="relative">
                <div className="text-6xl serif text-[#D4AF37]/70 leading-none">0{i + 1}</div>
                <div className="serif text-xl text-[#6E2638] mt-2">{r.street}</div>
                <p className="text-sm mt-2 text-[#1A1817]/75 leading-relaxed">{r.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TOUR TYPES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="tour-types">
        <div className="text-center max-w-2xl mx-auto">
          <div className="stamp">Choose your walk</div>
          <h2 className="serif text-4xl md:text-5xl text-[#6E2638] mt-4">Four ways to explore</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {[
            { title: "Individual", desc: "Public tours with group packages — better per-head pricing from 3 guests up.", price: "From $35 · 3 for $90", to: "/individual", cta: "Book a seat", testid: "type-individual" },
            { title: "Corporate", desc: "Team offsites, offshore visitors, client entertainment. Custom start times, custom themes.", price: "$599 / tour", to: "/corporate", cta: "Enquire", testid: "type-corporate" },
            { title: "Schools", desc: "Curriculum-aligned excursions for Years 5–12. History, civics, and city geography — brought to life.", price: "$499 / tour", to: "/school", cta: "Enquire", testid: "type-school" },
            { title: "Yesteryear", desc: "60-minute presentations at retirement villages and aged-care venues. Warm, dignified Melbourne storytelling.", price: "Free", to: "/yesteryear", cta: "Request a talk", testid: "type-yesteryear" },
          ].map((c) => (
            <Link key={c.title} to={c.to} data-testid={c.testid}
              className="card-warm rounded-2xl p-8 block group">
              <div className="flex items-baseline justify-between">
                <div className="serif text-3xl text-[#6E2638]">{c.title}</div>
                <div className="serif text-lg text-[#D4AF37]">{c.price}</div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-[#1A1817]/80">{c.desc}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-[#6E2638] group-hover:gap-3 transition-all text-sm font-semibold">
                {c.cta} <ArrowRight size={16} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* REVIEWS — auto-fed from admin-approved submissions via /leave-a-review */}
      <RealReviewsSection />

      {/* QUIZ TEASER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="quiz-teaser">
        <div className="rounded-3xl overflow-hidden relative">
          <img
            src={IMAGES.parliament}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-[#1E4637]" />
          <img
            src={IMAGES.parliament}
            alt=""
            className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-40"
          />
          <div className="relative p-10 md:p-16 grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="text-xs tracking-[0.28em] uppercase text-[#E7C766]">
                Marvellous Melbourne Quiz
              </div>
              <h2 className="serif text-4xl md:text-5xl text-[#FDFBF7] mt-4 leading-tight">
                Ten questions. One score. How well do you really know Melbourne?
              </h2>
              <p className="text-[#F5EFEB]/85 mt-4 text-sm md:text-base leading-relaxed">
                From John Batman to Dame Nellie Melba — take Martin's ten-question history quiz and find out if you're a Melbourne Marvel or in urgent need of a walking tour.
              </p>
            </div>
            <div className="md:col-span-2 flex md:justify-end">
              <Link
                to="/quiz"
                data-testid="home-quiz-cta"
                className="btn-primary bg-[#FDFBF7] text-[#6E2638] hover:bg-[#E7C766] text-lg"
              >
                Take the quiz <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-24" data-testid="testimonials">
        <div className="absolute inset-0 paper-texture opacity-[0.08]" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Quote size={40} className="mx-auto text-[#D4AF37]" />
          <blockquote className="serif italic text-2xl md:text-4xl text-[#1A1817] mt-6 leading-snug transition-opacity duration-500">
            "{TESTIMONIALS[tIndex].quote}"
          </blockquote>
          <div className="mt-6 text-sm">
            <div className="serif text-[#6E2638] text-lg">{TESTIMONIALS[tIndex].author}</div>
            <div className="uppercase tracking-[0.2em] text-xs text-[#1E4637]">
              {TESTIMONIALS[tIndex].role}
            </div>
          </div>
          <div className="flex justify-center gap-2 mt-8">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setTIndex(i)}
                data-testid={`testimonial-dot-${i}`}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  i === tIndex ? "bg-[#6E2638] w-8" : "bg-[#E5D9C5]"
                }`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="rounded-3xl bg-[#6E2638] text-[#FDFBF7] p-12 md:p-20 relative overflow-hidden" data-testid="cta-banner">
          <img src={IMAGES.parliament} alt="Parliament House"
               className="absolute inset-0 w-full h-full object-cover opacity-20" />
          <div className="relative max-w-2xl">
            <div className="text-xs tracking-[0.28em] uppercase text-[#E7C766]">Ready to walk?</div>
            <h2 className="serif text-4xl md:text-6xl mt-4 leading-tight">
              Melbourne is best read at walking pace.
            </h2>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/individual" className="btn-primary bg-[#FDFBF7] text-[#6E2638] hover:bg-[#E7C766]" data-testid="final-cta-book">
                Book a Tour
              </Link>
              <Link to="/contact" className="btn-outline-gold text-[#FDFBF7] border-[#E7C766]" data-testid="final-cta-contact">
                Ask Martin a question
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function RealReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchPublicReviews()
      .then((d) => setReviews(Array.isArray(d) ? d : []))
      .catch(() => setReviews([]))
      .finally(() => setLoaded(true));
  }, []);

  const display = reviews.slice(0, 8);

  return (
    <section className="bg-[#F5EFEB] border-y border-[#E5D9C5] py-24" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <div className="stamp">Fresh from the reviews</div>
          <h2 className="serif text-4xl md:text-5xl text-[#6E2638] mt-4">Straight from the walkers.</h2>
          <p className="text-[#1A1817]/75 mt-4 text-sm">
            Every review below is from a real guest who's just finished the walk.
          </p>
        </div>
        <OrnamentalDivider label="5-star tours" />

        {loaded && display.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            {display.map((r) => (
              <div key={r.id} className="card-warm rounded-2xl p-6 flex flex-col" data-testid={`walker-review-${r.id}`}>
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: r.rating }).map((_, j) => (
                    <Star key={j} size={14} className="text-[#D4AF37]" fill="#D4AF37" />
                  ))}
                  {Array.from({ length: 5 - r.rating }).map((_, j) => (
                    <Star key={`e${j}`} size={14} className="text-[#E5D9C5]" />
                  ))}
                </div>
                <blockquote className="serif italic text-[#1A1817] text-base leading-snug flex-1">
                  &ldquo;{r.quote}&rdquo;
                </blockquote>
                <div className="mt-4 pt-4 border-t border-[#E5D9C5] flex items-end justify-between">
                  <div>
                    <div className="serif text-[#6E2638]">{r.author_name}</div>
                    {r.tour_date && (
                      <div className="text-xs uppercase tracking-[0.2em] text-[#1E4637] mt-0.5">
                        Walked {new Date(r.tour_date).toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.24em] px-2 py-1 rounded-full bg-[#1E4637] text-[#FDFBF7]">
                    Verified walker
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : loaded ? (
          <div className="mt-6 card-warm rounded-2xl p-10 text-center" data-testid="no-reviews-yet">
            <Quote size={40} className="mx-auto text-[#D4AF37]" />
            <p className="serif text-2xl text-[#6E2638] mt-4">Fresh reviews landing here soon.</p>
            <p className="text-sm text-[#1A1817]/70 mt-2 max-w-lg mx-auto">
              Once walkers submit reviews after their tour, the best ones appear here.
              {GOOGLE_REVIEW_URL && <> In the meantime, you can read Google reviews below.</>}
            </p>
          </div>
        ) : null}

        {/* Leave-a-review CTA */}
        <div className="mt-14 flex flex-col md:flex-row items-center justify-between gap-6 rounded-2xl bg-[#FDFBF7] border border-[#E5D9C5] px-6 py-5">
          <div>
            <div className="serif text-xl text-[#6E2638]">Just finished the walk?</div>
            <p className="text-sm text-[#1A1817]/75 mt-1">
              Leave Martin a rating — it takes 30 seconds.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
            <Link
              to="/leave-a-review"
              className="btn-primary"
              data-testid="home-leave-review-cta"
            >
              <MessageSquarePlus size={16} /> Leave a review
            </Link>
            {GOOGLE_REVIEW_URL && (
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#4285F4] text-[#4285F4] px-5 py-2.5 font-semibold hover:bg-[#4285F4] hover:text-white transition-colors"
                data-testid="home-google-review-cta"
              >
                Review on Google <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
